import { Menu } from './menu';
import { ContextualMenuItemType } from '../components/ContextualMenu/constants';
import { AuthMethod } from '../containers/MultiAppAuthentication/constants';

namespace Api {

	export enum DriveItemType {
		Directory = 0,
		Component = 1,
		File = 2,
		Template = 3,
		Image = 4,
		Movie = 5,
		Publication = 6,
		Sound = 7,
		V2Gateway = 8,
		Link = 9,
		AppContainer = 10,
		Gallery = 11,
		ExternalLink = 12,
	}

	export enum AdditionalItemType {
		Note = 'Note',
	}

	export type AssetType = DriveItemType | AdditionalItemType;

	export interface NestedAppMeta {
		app: NestedDriveItems;
		sharedDir: NestedDriveItems;
	}

	export interface DriveItemResource {
		id: number;
		block?: number;
		type: DriveItemType;
		position?: number;
		format?: PublicationFormat;
	}

	export enum AspFolderDisplayType {
		List = 'LIST',
		Grid = 'GRID',
		Masonry = 'MASONRY',
		Coverflow = 'COVERFLOW',
	}

	export interface Dependency {
		id: number;
		version: number;
		type?: OutcomingType;
	}

	// TODO: separate typing by type, example, it seems only ExternalLink can have an url attribute
	export interface DriveItem {
		id: number;
		resolution?: string;
		label: string;
		name: string;
		originalFile: string;
		type: DriveItemType;
		src: string;
		version: number;
		icon?: string;
		titles: { [k: string]: string };
		associatedResolutions?: { [k: number]: string }; // sometimes not present in the api response
		parent: number;
		descriptions: { [k: string]: string };
		originalFileSize?: number;
		creationDate: string;
		lastUpdateDate: string;
		endDate?: string;
		pages?: number;
		url?: string;
		thumbsPath?: string;
		format?: PublicationFormat;
		privateDirId?: number;
		sharedDirId?: number;
		archiveDirId?: number;
		sourcesDirId?: number;
		displayMode?: AspFolderDisplayType;
		status?: AssetItemStatus;
		componentType?: ComponentType;
		fileType?: AssetItemFileFormats;
		proposalStats?: ProposalData;
		linkId?: number;
		position?: number;
		tags: string[];
		availableTags?: string[];
		bgMedia?: string;
		bgColor?: string;
		defaultCover?: string;
		linkParentId?: number;
		published?: boolean;
		dependencies?: Dependency[];
		isTemplate?: boolean;
	}

	export interface NestedDriveItems extends DriveItem {
		resources?: NestedDriveItems[];
		resolutions?: { [k: string]: ResolutionGroup };
	}
	// same as driveItem but delivered by meta.json API
	export interface DriveFolderInfo extends DriveItem {
		blocksNumber?: number;
		resolutions?: { [k: string]: ResolutionGroup };
		resources?: DriveItemResource[];
		resolutionsBlock?: number;
	}

	export interface ResolutionSize {
		box: boolean;
		extension: string;
		width: number;
		height: number;
		ignoreUpscale: boolean;
	}

	export interface ResolutionGroup {
		sizes: ResolutionSize[];
		supported: string[];
	}

	export interface DriveItemContentBlock {
		metas: { [k: number]: DriveItem };
		resolutions?: { [k: string]: ResolutionGroup };
	}

	export interface DriveItemContentBlockWithOrderedItems extends DriveItemContentBlock {
		ordered: number[];
	}

	export enum OutcomingType {
		Directory = 'DIRECTORY',
		Component = 'COMPONENT',
		File = 'FILE',
		Template = 'TEMPLATE',
		Image = 'IMAGE',
		Movie = 'MOVIE',
		Publication = 'PUBLICATION',
		Sound = 'SOUND',
		V2Gateway = 'V2GATEWAY',
		Link = 'LINK',
		AppContainer = 'APP_CONTAINER',
		Gallery = 'GALLERY',
		ExternalLink = 'EXTERNAL_LINK'
	}

	export interface Outcoming {
		external?: boolean;
		id: number;
		type: OutcomingType;
		version: string;
		outcomings?: Outcoming[];
		resource?: Outcoming;
	}

	export interface Config {
		applicationGallery: number;
		applicationUrl: string;
		clientId: number;
		locale: string;
		type: number;
		mediaPath?: string;
		service?: Service;
		downloadAllPopup?: DownloadAllPopupConfig;
		importantAssets?: ImportantAssetsConfig;
		breadcrumbs?: BreadcrumbsConfig;
		dependencies?: DependenciesConfig;
		offlineDownload?: OfflineDownloadConfig;
		specialPrivateDirIds?: number[];
		enableEditorTagBasedEditScope?: boolean;
	}

	export interface Template {
		commonStyles: CommonStyles;
		sharedMethods?: ShareMethod[];
		sharedNetworks?: ShareNetwork[];
		brand?: BrandInfos;
		welcomepage?: WelcomePage;
		locale: string;
		menu?: Menu;
		applicationSplash?: ApplicationSplash;
		search?: SearchConfig;
		cookie?: CookiePolicy;
		publicationSettings?: Api.PublicationSettingName[];
		hasCustomCSS?: boolean;
		hasCustomJS?: boolean;
		tagClasses?: string[];
		contextMenuOptions?: ContextMenuOptions;
	}

	export interface SearchConfig {
		showTags: boolean;
		hasTagTrail?: boolean;
		hasDownloadPDF?: boolean;
		categorySeparator?: string;
	}

	export interface CookiePolicy {
		enabled: 'true' | 'false';
		gdpr: 'true' | 'false';
		url?: string;
	}

	export interface ContextMenuOptions {
		alwaysHidden?: ContextualMenuItemType[];
	}

	export interface ApplicationSplash {
		background?: string;
		image?: string;
		spinnerWheelColor?: string;
	}

	export interface WelcomePage {
		url: string;
	}

	export interface BrandInfos {
		url: string;
		name: string;
		logo: string;
		logoHome: string;
	}

	export interface ImportantAssetsConfig {
		enabled: boolean;
		tags: string[];
		isDownloadedOnLaunch: boolean;
	}

	interface BreadcrumbsConfig {
		isCustomHomeLink?: boolean;
	}

	interface DependenciesConfig {
		exclude: DependenciesExcludeConfig;
	}

	interface DependenciesExcludeConfig {
		ids: number[];
		types: Api.OutcomingType[];
	}

	export interface ResourceZipExcludeRule {
		ids: number[];
		exclude: string[];
	}

	export interface ResourceZipExcludeConfig {
		enabled?: boolean;
		rules?: ResourceZipExcludeRule[];
		defaultExclude?: string[];
	}

	export interface StaleOfflineCleanupConfig {
		enabled?: boolean;
		autoCleanDefault?: boolean;
		showCleanupNotification?: boolean;
	}

	export interface OfflineDownloadConfig {
		resourceZipExclude?: ResourceZipExcludeConfig;
		staleOfflineCleanup?: StaleOfflineCleanupConfig;
	}

	interface DownloadAllPopupConfig {
		enabled: boolean;
	}

	export interface CommonStyles {
		primary: PrimaryStyle;
	}

	export interface PrimaryStyle {
		actionColor: string;
		backgroundColor: string;
		cardColor: string;
		contentColor: string;
		spacerColor: string;
		strongColor: string;
		topnavColor: string;
		iconsColor: string;
		topnavOpacity: number;
		thumbBorderColor: string;
	}

	export interface SecondaryStyle {
		activeColor: string;
		alertColor: string;
		altContentColor: string;
		altPopupColor: string;
		notificationColor: string;
	}

	export interface TouchStyle {
		primaryBackgroundColor: string;
		primaryCardColor: string;
		primaryContentColor: string;
		pushmenuBackgroundColor: string;
		secondaryAlertColor: string;
	}

	export interface PushMenuStyle {
		activeColor: string;
		backgroundColor: string;
		contentColor: string;
		iconColor: string;
		spacerColor: string;
		strongBackgroundColor: string;
		strongColor: string;
	}

	export interface Service {
		host: string;
		directHost?: string;
		token: string;
		email: EmailService;
		download: DownloadService;
	}

	export interface EmailService {
		service: string;
	}

	export interface DownloadService {
		service: string;
	}

	export enum ShareMethod {
		Download = 'download',
		Share = 'share'
	}

	export enum ShareNetwork {
		Uprez = 'email',
		Copy = 'copy'
	}

	export enum AssetItemAccessType {
		PUBLIC = 'PUBLIC',
	}

	export enum AssetItemInvoiceStatus {
		DRAFT = 'DRAFT',
		DEMO = 'DEMO',
		TO_BE_INVOICED = 'TOBEINVOICED',
		INVOICED = 'INVOICED',
		SALESFORCE = 'SALESFORCE',
		PAID = 'PAID',
		CHARMED = 'CHARMED',
		SUSPENDED = 'SUSPENDED',
		PUBLISHED = 'PUBLISHED',
		UNPUBLISHED = 'UNPUBLISHED',
	}

	export enum AssetItemStatus {
		LIVE = 'LIVE',
		PROCESSING = 'PROCESSING',
		ERROR = 'ERROR',
	}

	export enum AssetItemFileTypes {
		DIRECTORY = 'DIRECTORY',
		COMPONENT = 'COMPONENT',
		GALLERY = 'GALLERY',
		IMAGE = 'IMAGE',
		PUBLICATION = 'PUBLICATION',
		TEMPLATE = 'TEMPLATE',
		MOVIE = 'MOVIE',
		FILE = 'FILE',
		V2GATEWAY = 'V2GATEWAY',
		EXTERNAL_LINK = 'EXTERNAL_LINK',
		APP_CONTAINER = 'APP_CONTAINER',
		LINK = 'LINK',
		PROPOSAL = 'PROPOSAL',
		DEFAULT = 'DEFAULT',
	}

	export enum AssetItemFileFormats {
		DOCUMENT = 'DOCUMENT',
		PRESENTATION = 'PRESENTATION',
		SPREADSHEET = 'SPREADSHEET',
		INDESIGN = 'INDESIGN',
		ILLUSTRATOR = 'ILLUSTRATOR',
		PHOTOSHOP = 'PHOTOSHOP',
		FILE = 'FILE',
		PDF = 'PDF',
		JPG = 'JPG',
		JPEG = 'JPEG',
		PNG = 'PNG',
		SVG = 'SVG',
		GIF = 'GIF',
		MOV = 'MOV',
		MP4 = 'MP4',
		AVI = 'AVI',
	}

	export interface AspDriveResourceItem {
		accessType: AssetItemAccessType;
		beginDate: {
			date: number;
			month: number;
			year: number;
		};
		clientId: number;
		clonable: boolean;
		endDate: number | null;
		expired: boolean;
		filesize: number;
		folder: boolean;
		forbidUpdateDate: number | null;
		forbiddenUpdate: boolean;
		genericItemCreationDate: number;
		genericItemId: number;
		genericItemLastUpdate: number;
		genericItemName: string;
		globalId: number;
		iconName: string | null;
		invoiceStatus: AssetItemInvoiceStatus;
		label: string;
		modelId: number;
		modelName: string;
		parentDriveGId: number | null;
		position: number;
		pushable: boolean;
		referent: AspDriveResourceItem | null;
		relUrl: string;
		secured: boolean;
		size: number;
		status: AssetItemStatus;
		type: AssetItemFileTypes;
		fileType: AssetItemFileFormats;
		format: PublicationFormat;
		version: number;
		sendingDate: number;
		openingDate: number;
		readsNbr: number;
		recipientEmail: string;
		downloads?: number;
		resolution?: string;
	}

	export interface AspDriveResourceItemLight {
		globalId: number | null;
		label: string;
		genericItemId: number | null;
		parentDriveGId: number | null;
		relUrl: string;
		externalRelUrl: string;
		isExternalLink: boolean;
		type: Api.AssetItemFileTypes;
		deps?: string;
		iconName?: string | null;
		resolution?: string;
	}

	export interface AppLoginResponse {
		login: string;
		clientId: number;
		associatedApps: AspDriveResourceItem[];
		token: string;
		oneSessPerUser: boolean;
		org?: string;
		email?: string;
	}

	export interface AuthMethodsResponse {
		orgAuthMethods: AuthMethod[];
	}

	export enum CustomerContextRoles {
		ROLE_ALL_RIGHTS = 'ROLE_ALL_RIGHTS', // Super Admin
		ROLE_PRODUCTION = 'ROLE_PRODUCTION', // Prod
		ROLE_COMMERCIAL_SALESFORCE = 'ROLE_COMMERCIAL_SALESFORCE', // Commercial Salesforce
		ROLE_COMMERCIAL_MANUAL = 'ROLE_COMMERCIAL_MANUAL', // Commercial
		ROLE_AGENCY = 'ROLE_AGENCY', // Agency (external commercials)
		ROLE_SAAS_ACCOUNT_MANAGER = 'ROLE_SAAS_ACCOUNT_MANAGER', // Account manager
		ROLE_SAAS_USER = 'ROLE_SAAS_USER', // Editor
		ROLE_CONSUMER = 'ROLE_CONSUMER', // Customer
		ROLE_APPLICATION_USER = 'ROLE_APPLICATION_USER', // User Editor
		ROLE_APPLICATION_VIEWER = 'ROLE_APPLICATION_VIEWER', // User Library
		ROLE_LIBRARY_ACCOUNT_MANAGER = 'ROLE_LIBRARY_ACCOUNT_MANAGER', // Library Account manager
	}

	export interface CustomerAccount {
		autoRenew: boolean;
		availableCredits: number;
		company: string;
		creationDate: number;
		email: string;
		id: number;
		label: string;
		licenceId: number;
		locale: string;
		parentId: number;
		phone: string;
		role: CustomerContextRoles;
		saleName: string;
		status: 'ACTIVE' | 'DISABLED';
		type: string;
	}

	export interface GetClosestServerResponse {
		servers: { [k: string]: ServerInfos };
	}

	export interface ServerInfos {
		domain: string;
		closestServer: boolean;
	}

	export interface SharedContactInfo {
		company?: string;
		forename?: string;
		email: string;
		name?: string;
		phone?: string;
		position?: string;
	}

	interface SharedResource {
		id: number;
		url: string;
	}

	export interface SharedData {
		fromName: string;
		fromEmail: string;
		toAddresses: string[];
		message: string;
		language: string;
		resources: SharedResource[];
		notes?: NoteData[];
		contactInfo?: SharedContactInfo | {};
		subject?: string;
		createDeal?: boolean;
		forCopy?: boolean;
	}

	export interface CreateFolderData {
		driveLabel: string;
		clientId: number;
		parentGId: number;
		resourcesGIds: number[];
		displayMode?: string;
	}

	export enum PublicationDisplayMode {
		AUTO = 'AUTO',
		CROSS_NAV = 'CROSS_NAV',
		SLIDER = 'SLIDER',
		FLIPBOOK = 'FLIPBOOK',
	}

	export enum PublicationFirstFrameMode {
		SINGLE = 'SINGLE',
		DOUBLE = 'DOUBLE',
	}

	export interface IndesignEditorGeneralData {
		globalId: number;
		format: PublicationFormat;
		displayMode: PublicationDisplayMode;
		firstFrameMode: PublicationFirstFrameMode;
		label: string;
		totalPages: number;
		pagePath: string;
		imagePath: string;
		thumbnailPath: string;
		spreadPath?: string;
		cssPath?: string;
		themesCssPath?: string;
		jsPath?: string;
		configsPath?: string;
		nonEpubPages?: number[];
		dimensions?: { width: number, height: number }; // only for pdf publications
		invoiceStatus?: AssetItemInvoiceStatus;
		accessiblePagePath?: string;
		accessiblePages?: number;
		accessibleUrl?: string;
	}

	export interface EditorThumbnailData {
		label: string;
		position: number;
		version?: number;
		extPage?: boolean;
	}

	export interface IndesignEditorData {
		enrichmentInfo: IndesignEditorGeneralData;
		pagesData: EditorThumbnailData[];
	}

	export interface HtmlEditorData {
		label: string;
		pagePath: string;
	}

	export interface ImportFileData {
		clientId: number;
		resourceGId: number;
		resourceId: number;
		hardName: string;
	}

	export interface PublicationProgressData {
		percentage: number;
		status: AssetItemStatus;
	}

	export enum ComponentType {
		EPUB = 'EPUB',
		PDF = 'PDF',
		ZIP = 'ZIP',
		ZIP_EDITABLE = 'ZIP_EDITABLE',
	}

	export enum PublicationFormat {
		PDF = 'PDF',
		PDF_EDITABLE = 'PDF_EDITABLE',
		EPUB = 'EPUB',
		EPUB_MASTER = 'EPUB_MASTER',
		EPUB_EDITABLE = 'EPUB_EDITABLE',
		EPUB_PRESENTATION = 'EPUB_PRESENTATION',
		EPUB_PUBLI_MASTER = 'EPUB_PUBLI_MASTER',
		EPUB_MODIFIABLE = 'EPUB_MODIFIABLE',
		EPUB_PREVIEW = 'EPUB_PREVIEW',
		EPUB_PLAYLIST = 'EPUB_PLAYLIST',
	}

	export enum ProposalStatus {
		DRAFT = 'DRAFT',
		SENT = 'SENT',
		SEEN = 'SEEN',
	}

	export interface ProposalData {
		status: ProposalStatus;
		forbiddenUpdate: boolean;
		editable: boolean;
		sentOn?: string;
		openedOn?: string;
		readsNbr?: number;
	}

	export interface PublicationTemplatesData {
		masterProposals: DriveItem[];
		customTemplates: DriveItem[];
		resolutions: { [k: string]: ResolutionGroup };
	}

	export interface SharePublicationData {
		pin: string;
		resourceUrl: string;
	}

	export enum CreationSource {
		DESKTOP = 'DESKTOP',
		PLUGIN = 'PLUGIN',
	}

	export interface RenameResourceData {
		newLabel: string;
	}

	export interface TrashResourcesData {
		undeletableResources: {
			[k: number]: string; // key is the id of the resource and the value is its label
		};
	}

	export enum CombineOption {
		ADVANCED = 'ADVANCED',
		MULTISELECT = 'MULTISELECT',
		PLAYLIST = 'PLAYLIST',
	}

	export interface ProfileData {
		id: number;
		clientId: number;
		name: string;
		email: string;
		position: string;
		phone: string | null;
		password: null;
		confirmPassword: null;
		photoPath: string | null;
		photoConfig: string | null;
		crmProviders: CRMProviderData[];
		emailProvider: EmailProviderData;
		role: CustomerContextRoles;
		oneSessPerUser: boolean;
		combineOptions: CombineOption;
		actionCredits: ActionCredits;
		customer: Customer;
	}

	export interface EditProfileData {
		profileInfo: string;
		profilePic?: File;
		removeProfilePic?: string;
	}

	export interface ProfilePhotoConfig {
		name: string;
		x: number;
		y: number;
		width: number;
		height: number;
	}

	export enum CRMProviders {
		NONE = 'NONE',
		HUBSPOT = 'HUBSPOT',
		SALESFORCE = 'SALESFORCE',
	}

	export interface CRMProviderData {
		provider: CRMProviders;
		integrationUrl: string;
		enabled: boolean;
		clientId?: string;
		clientSecret?: string;
	}

	enum EmailProviders {
		NYLAS = 'NYLAS',
	}

	export interface EmailProviderData {
		provider: EmailProviders;
		integrationUrl: string;
		enabled: boolean;
		email?: string;
	}

	export interface CrmContact {
		email: string | null;
		firstName: string | null;
		lastName: string | null;
		company: string | null;
	}

	// When the client is not connected to a CRM,
	// we get a status 304 with no body as a response
	export type getCrmContactsData = Api.CrmContact[] | '';

	export interface ProposalsByTypeData {
		list: AspDriveResourceItem[];
		currentPage: number;
		totalPage: number;
	}

	export interface NoteData {
		id: number;
		date: number;
		title: string;
		content: string;
		drawing: string;
		resourceClientId?: number;
		resourceGId?: number;
		uuid?: string;
		modifiedOn?: number;
		replyTo?: number;
		replies?: NoteData[];
	}

	export interface NotesData {
		notes: NoteData[];
	}

	export enum PublicationSettingName {
		customUrl = 'customUrl',
		downloadPdf = 'downloadPdf',
		googleAnalyticsUa = 'googleAnalyticsUa',
	}

	export interface PublicationSettings {
		customUrl?: string | null;
		googleAnalyticsUa?: string | null;
		[key: string]: any; // tslint:disable-line no-any
	}

	export interface PublicationIntegration {
		projectUrl: string;
	}

	export enum PublicationPdfOption {
		FULL = 'full',
		CUSTOM = 'custom',
	}

	export enum PublicationPdfImportResponse {
		SYNC = 'SYNC',
		ASYNC = 'ASYNC',
	}

	export interface PublicationPdfSettings {
		pdfOption: PublicationPdfOption;
		label?: string;
		globalId?: number;
	}

	export interface ShareUser {
		id: number;
		email: string;
		name: string;
	}

	export enum AccountType {
		POSTPAID = 'POSTPAID',
		PREPAID = 'PREPAID',
	}

	export interface ActionCredits {
		PUBLISH_EPUB: number;
	}

	export interface Customer {
		userId: number;
		genericItemId: number;
		genericItemName: string;
		email?: string;
		label: string;
		accType: AccountType;
		role: CustomerContextRoles;
	}

	export interface CustomerContext {
		actionCredits: ActionCredits;
		customer: Customer;
	}

	export enum OutcomingsLevel {
		ALL = 'ALL',
		TREE = 'TREE',
	}

	export interface ComboWithExternalSpreadsData {
		clientId: number;
		globalId: number;
		id: number;
		srcPubliId: number;
	}

	export interface PublishPrice {
		actionCredits: CustomActionCredits;
		clientId?: number;
		globalId?: number;
	}

	export interface CustomActionCredits {
		PUBLISH?: number;
		CUSTOM_DOMAIN?: number;
		WISHLIST?: number;
		XLS_SECURITY?: number;
		SSO_SECURITY?: number;
	}
}

export { Api };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/typings/api.ts